import { MK_AGENCY_ID } from "../env";
import { useSkipioQuery } from "./use-query";
import { useQuery } from "@tanstack/react-query";
import { FeatureFlag, fetchUser } from "../api/users";

const featuresBase = {
  call_forwarding: false,
  campaign_package_creation: false,
  community__cms: false,
  events: false,
  outbound_calling: false,
  paused: false,
  sentiment: false,
  teams: false,
  voice_broadcasts: false,
  web2txt: false,
};

type Plan = {
  name: string;
  skipioPlan: any;
  isMk: boolean;
  isMultiUser: boolean;
  isMultiUserOwner: boolean;
  isAccountOwner: boolean;
  id: string | null;
  price: number | null;
  billingInterval: "month" | "year";
  isFree: boolean;
  canUpgrade: boolean;
  availablePlans: string[];
  features: Partial<{ [key in FeatureFlag]: boolean }>;
  percentOfMessagesUsed: number;
  canSendMoreScheduledMessages: boolean;
  canUploadContacts: boolean;
  contactsRemaining: number;
  remainingScheduledMessages: number;
  sentMessagesPercent: number;
  monthlySmsMessagesSent: number;
  maxMonthlyAutomatedMessages: number;
  maxDailyAutomatedMessages: number;
  usedContactsPercent: number;
  canMakeDripCampaign: boolean;
  maxDripCampaigns: number;
  dripCampaignsRemaining: number;
  canMakeReminderCampaign: boolean;
  maxReminderCampaigns: number;
  reminderCampaignsRemaining: number;
  reminderCampaignsCount: number;
  canMakeEventCampaign: boolean;
  maxEventCampaigns: number;
  eventCampaignsRemaining: number;
  canMakeGroup: boolean;
  maxContactGroups: number;
  groupsRemaining: number;
  maxContactsInEvent: number;
  maxContacts: number;
  maxContactsInScheduledMessage: number;
  contactsCount: number;
  dripCampaignsCount: number;
  maxRuleBasedAutomations: number;
  canMakeRuleBasedAutomation: boolean;
  monthlyVoicemailMessagesSent: number;
  maxMonthlyVoicemailMessages: number;
  voicemailMessagesRemaining: number;
  automatedSmsOverageRate: number | null;
  voicemailOverageRate: number | null;

  addons: Addon[];
};

export type Addon = {
  id: string;
  additionalCount: number;
  addonType: "contact" | "user";
  name: string;
  price: string;
  quantity?: number;
};

export default function usePlan(): Plan {
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;

  const plan = useSkipioQuery({
    queryKey: ["skipioPlans", "me"],
    enabled: user.account.skipioPlanId !== null,
  }).data;

  const { possibleChanges = {} } =
    useSkipioQuery({
      queryKey: ["skipioPlans"],
      suspense: false,
      useErrorBoundary: true,
    }).meta || {};

  let availablePlans = possibleChanges[user.account.skipioPlanId] || [];

  const percentOfMessagesUsed = Math.min(
    Math.ceil(
      (user.skipioPlan.monthlySmsMessagesSent /
        user.account.maximumMonthlySmsMessages) *
        100
    ),
    100
  );

  const features: Partial<{ [key in FeatureFlag]: boolean }> = {
    ...featuresBase,
    ...Object.fromEntries(user.enabledFeatures.map((name) => [name, true])),
  };

  const agencyId =
    user.id && user.account.belongsToAgency
      ? user.account.belongsToAgency.id
      : null;

  const isMk = agencyId === MK_AGENCY_ID;

  return {
    name: String(plan.name),
    skipioPlan: plan,
    isMk,
    isMultiUser: user.account.maximumUsers > 1,
    isMultiUserOwner: user.account.maximumUsers > 1 && user.accountOwner,
    isAccountOwner: user.accountOwner,
    id: plan.id,
    price: Number(plan.price),
    billingInterval: plan.billingInterval,
    isFree: plan.automatedSmsOverageRate === null && Number(plan.price) === 0,
    canUpgrade: availablePlans.length > 0,
    availablePlans,
    features,
    // messages
    percentOfMessagesUsed,
    monthlySmsMessagesSent: user.skipioPlan.monthlySmsMessagesSent,
    canSendMoreScheduledMessages:
      plan.automatedSmsOverageRate !== null ||
      user.account.maximumMonthlySmsMessages >=
        user.skipioPlan.monthlySmsMessagesSent,
    sentMessagesPercent:
      (user.skipioPlan.monthlySmsMessagesSent /
        user.account.maximumMonthlySmsMessages) *
      100,
    maxMonthlyAutomatedMessages: plan.maxMonthlyAutomatedMessages,
    maxDailyAutomatedMessages:
      plan.maxDailyAutomatedMessages === null
        ? Infinity
        : plan.maxDailyAutomatedMessages,
    remainingScheduledMessages: Math.max(
      0,
      user.account.maximumMonthlySmsMessages -
        user.skipioPlan.monthlySmsMessagesSent
    ),
    // contacts
    canUploadContacts:
      plan.maxContacts === null ||
      plan.maxContacts > user.account.contactsCount,
    maxContacts: plan.maxContacts === null ? Infinity : plan.maxContacts,
    contactsCount: user.account.contactsCount,
    contactsRemaining:
      plan.maxContacts === null
        ? Infinity
        : Math.max(0, plan.maxContacts - user.account.contactsCount),
    usedContactsPercent: (user.account.contactsCount / plan.maxContacts) * 100,
    // drip campaigns
    dripCampaignsCount: user.skipioPlan.dripCampaignsCount,
    reminderCampaignsCount: user.skipioPlan.reminderCampaignsCount,
    canMakeDripCampaign:
      plan.maxDripCampaigns === null
        ? true
        : plan.maxDripCampaigns > user.skipioPlan.dripCampaignsCount,
    maxDripCampaigns: plan.maxDripCampaigns,
    dripCampaignsRemaining:
      plan.maxDripCampaigns === null
        ? Infinity
        : Math.max(
            0,
            plan.maxDripCampaigns - user.skipioPlan.dripCampaignsCount
          ),
    // reminder campaigns
    canMakeReminderCampaign:
      plan.maxReminderCampaigns === null
        ? true
        : plan.maxReminderCampaigns > user.skipioPlan.reminderCampaignsCount,
    maxReminderCampaigns: plan.maxReminderCampaigns,
    reminderCampaignsRemaining:
      plan.maxReminderCampaigns === null
        ? Infinity
        : Math.max(
            0,
            plan.maxReminderCampaigns - user.skipioPlan.reminderCampaignsCount
          ),
    // reminder campaigns
    canMakeEventCampaign:
      plan.maxEventCampaigns === null
        ? true
        : plan.maxEventCampaigns > user.skipioPlan.eventCampaignsCount,
    maxEventCampaigns: plan.maxEventCampaigns,
    eventCampaignsRemaining:
      plan.maxEventCampaigns === null
        ? Infinity
        : Math.max(
            0,
            plan.maxEventCampaigns - user.skipioPlan.eventCampaignsCount
          ),
    // groups
    canMakeGroup:
      plan.maxContactGroups === null
        ? true
        : plan.maxContactGroups > user.contactGroupsCount,
    maxContactGroups: plan.maxContactGroups,
    maxContactsInScheduledMessage:
      plan.maxContactsInScheduledMessage === null
        ? Infinity
        : plan.maxContactsInScheduledMessage,
    groupsRemaining:
      plan.maxContactGroups === null
        ? Infinity
        : Math.max(0, plan.maxContactGroups - user.contactGroupsCount),
    // events
    maxContactsInEvent:
      plan.maxContactsInEvent === null ? Infinity : plan.maxContactsInEvent,

    //rules
    maxRuleBasedAutomations: plan.maxRuleBasedAutomations,
    canMakeRuleBasedAutomation:
      plan.maxRuleBasedAutomations === null
        ? true
        : plan.maxRuleBasedAutomations > user.skipioPlan.automationsCount,

    // voicemail
    monthlyVoicemailMessagesSent: user.skipioPlan.monthlyVoicemailMessagesSent,
    maxMonthlyVoicemailMessages:
      plan.maxMonthlyVoicemailMessages === null
        ? Infinity
        : plan.maxMonthlyVoicemailMessages,
    voicemailMessagesRemaining: Math.max(
      0,
      plan.maxMonthlyVoicemailMessages -
        user.skipioPlan.monthlyVoicemailMessagesSent
    ),

    // overages
    automatedSmsOverageRate: Number(plan.automatedSmsOverageRate),
    voicemailOverageRate: Number(plan.voicemailOverageRate),

    addons: plan.addons,
  };
}
